import React from 'react';
import { HomeIcon, CarIcon, BuildingIcon, WavesIcon, ConeIcon, CalendarIcon, ArrowUpIcon, ArrowDownIcon, ArrowRightIcon, LightbulbIcon, BarChart3Icon, Maximize2Icon, LayoutDashboardIcon, FileStackIcon, ClipboardCheckIcon, CreditCardIcon, FolderIcon, LineChartIcon, FileTextIcon, SettingsIcon, LeafIcon, ChevronDownIcon } from 'lucide-react';
import { PERMIS_IMAGES } from '../../../data/permisHero';
import { volumeByType, processingTimes, funnel, sectorMarkers, sectorLegend, atnaInsight, dashboardMeta, type Bi, type TypeKey } from '../../../data/permisAnalytics';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
export const PANEL = 'rounded-2xl border border-[#EEEEF4] bg-white p-4';
const TYPE_ICONS: Record<TypeKey, typeof HomeIcon> = {
  reno: HomeIcon,
  parking: CarIcon,
  rental: BuildingIcon,
  pool: WavesIcon,
  public: ConeIcon,
  event: CalendarIcon
};
function PanelTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="font-sans text-[13px] font-semibold text-[#121A3A]" data-id="element-4721" data-mp="2wwgxbfb7hs7a">{children}</h3>;
}
export function VolumePanel({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={PANEL} data-id="element-4722" data-mp="2n6dwwu1r9byx">
      <PanelTitle data-id="element-4723" data-mp="2n5mcmfdlz0ny">{pick(dashboardMeta.panels.volume)}</PanelTitle>
      <ul className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2 gap-y-5" data-id="element-4724" data-mp="2j6n6wsk1yzvg">
        {volumeByType.map(t => {
        const Icon = TYPE_ICONS[t.key];
        return <li key={t.key} className="flex flex-col items-center text-center min-w-0" data-id="element-4725" data-mp="297xp0zs5pff7">
              <div className="h-[92px] w-full flex items-end" data-id="element-4726" data-mp="248l6gr8biuif">
                <div className="w-full rounded-t-md rounded-b-sm flex items-end justify-center pb-2" style={{
              height: `${t.height}%`,
              backgroundColor: t.color
            }} aria-hidden="true" data-id="element-4727" data-mp="24936lyy3psvm">
                  <Icon className="w-5 h-5 text-white" strokeWidth={2} data-id="element-4728" data-mp="3ukhcugp5oqxg" />
                </div>
              </div>
              <span className="mt-2 font-sans text-[17px] font-semibold text-[#121A3A]" data-id="element-4729" data-mp="1ry3og46b3eh9">{t.value}</span>
              <span className="font-sans text-[9.5px] text-[#5B6478] leading-tight min-h-[24px]" data-id="element-4730" data-mp="1wq3qq39umjh0">{pick(t.label)}</span>
              <span className="mt-1.5 flex items-center gap-0.5 font-sans text-[10.5px] font-semibold text-[#1E8A45]" data-id="element-4731" data-mp="1iz8eqi41zm97">
                <ArrowUpIcon className="w-3 h-3" strokeWidth={2.4} aria-hidden="true" data-id="element-4732" data-mp="1mx669rlaqkfr" />
                {pick(t.trend)}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
export function TimesPanel({
  isFR
}: P) {
  const pick = pk(isFR);
  const max = 26;
  return <div className={`${PANEL} h-full`} data-id="element-4733" data-mp="21q9c51piz9qn">
      <PanelTitle data-id="element-4734" data-mp="26mqaeoqfv4hg">{pick(dashboardMeta.panels.times)}</PanelTitle>
      <ul className="mt-4 space-y-3" data-id="element-4735" data-mp="26pceufi193vg">
        {processingTimes.map((t, i) => <li key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-3" data-id="element-4736" data-mp="q7opaggv5byr" data-mpi={i}>
            <span className="font-sans text-[10.5px] text-[#3A4050] truncate" data-id="element-4737" data-mp="v8ungttalrfo">{pick(t.label)}</span>
            <span className="h-3 rounded-sm bg-[#EEEDFB] overflow-hidden" data-id="element-4738" data-mp="mprcxdtk90rx">
              <span className="block h-full rounded-sm bg-[#6F6BF2]" style={{
            width: `${t.days / max * 100}%`
          }} data-id="element-4739" data-mp="iq8o46ymc5jt" />
            </span>
            <span className="font-sans text-[10.5px] text-[#121A3A] w-[52px] text-right whitespace-nowrap" data-id="element-4740" data-mp="wogsgzxzi950">
              {t.days} {pick(dashboardMeta.days)}
            </span>
          </li>)}
      </ul>
    </div>;
}
export function FunnelPanel({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${PANEL} h-full`} data-id="element-4741" data-mp="3v8oz6uh09g1r">
      <PanelTitle data-id="element-4742" data-mp="dqw37rrb58jv">{pick(dashboardMeta.panels.funnel)}</PanelTitle>
      <ul className="mt-4 space-y-1.5" data-id="element-4743" data-mp="dsgyjjlg4vis">
        {funnel.map((f, i) => <li key={i} className="flex items-center gap-3" data-id="element-4744" data-mp="8y2zn77ks69d" data-mpi={i}>
            <span className="w-[92px] shrink-0 flex justify-center" aria-hidden="true" data-id="element-4745" data-mp="3hexpnm39xw61">
              <span className="block h-[30px]" style={{
            width: `${f.width}%`,
            backgroundColor: f.color,
            clipPath: 'polygon(0 0, 100% 0, 94% 100%, 6% 100%)'
          }} data-id="element-4746" data-mp="3cgntp99q6rjd" />
            </span>
            <span className="w-6 border-t border-dashed border-[#C9CCDA] shrink-0" aria-hidden="true" data-id="element-4747" data-mp="2opxoh3oy6jj8" />
            <span className="font-sans text-[12px] font-semibold text-[#121A3A] w-[42px] shrink-0" data-id="element-4748" data-mp="2tg5ql0can7wi">{pick(f.value)}</span>
            <span className="font-sans text-[10.5px] text-[#3A4050] truncate" data-id="element-4749" data-mp="2yga9sy4l6gqf">{pick(f.label)}</span>
          </li>)}
      </ul>
    </div>;
}
export function SectorsPanel({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${PANEL} h-full`} data-id="element-4750" data-mp="38e6izkdyr1lw">
      <div className="flex items-center justify-between" data-id="element-4751" data-mp="3cf03s4w400ak">
        <PanelTitle data-id="element-4752" data-mp="3cegx5kbwsybu">{pick(dashboardMeta.panels.sectors)}</PanelTitle>
        <Maximize2Icon className="w-3.5 h-3.5 text-[#9A9FB5]" aria-hidden="true" data-id="element-4753" data-mp="1e0p1ae08oyys" />
      </div>
      <div className="mt-3 flex flex-col sm:flex-row gap-4" data-id="element-4754" data-mp="1i0h2u6r7u4un">
        <div className="relative w-full sm:w-[190px] h-[190px] rounded-xl overflow-hidden shrink-0" data-id="element-4755" data-mp="1i3234rbv8cqa">
          <img src={PERMIS_IMAGES.parcel} alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-4756" data-mp="15qzs4j36tltd" />
          {sectorMarkers.map((m, i) => <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white flex items-center justify-center font-sans text-[12px] font-semibold text-white shadow-md" style={{
          left: `${m.x}%`,
          top: `${m.y}%`,
          width: m.size,
          height: m.size,
          backgroundColor: m.color
        }} data-id="element-4757" data-mp="10spv02xwkb54" data-mpi={i}>
              {m.value}
            </span>)}
          <span className="absolute right-2 bottom-2 w-6 h-6 rounded-md bg-white/90 flex items-center justify-center" aria-hidden="true" data-id="element-4758" data-mp="1ke2ounjorqfn">
            <Maximize2Icon className="w-3 h-3 text-[#3A4050]" data-id="element-4759" data-mp="90h1n7xdzywr" />
          </span>
        </div>
        <ul className="space-y-2.5 sm:pt-4" data-id="element-4760" data-mp="41no5fi3o7wl">
          {sectorLegend.map((l, i) => <li key={i} className="flex items-center gap-2 font-sans text-[10.5px] text-[#3A4050] whitespace-nowrap" data-id="element-4761" data-mp="440ccaegbei3" data-mpi={i}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{
            backgroundColor: l.color
          }} aria-hidden="true" data-id="element-4762" data-mp="ahnr2auoij68" />
              {pick(l.label)}
            </li>)}
        </ul>
      </div>
    </div>;
}
export function AtnaInsightPanel({
  isFR
}: P) {
  const pick = pk(isFR);
  const a = atnaInsight;
  return <div className="rounded-2xl bg-[#F4F3FD] border border-[#E7E5FA] p-3.5 h-full flex flex-col gap-3" data-id="element-4763" data-mp="3ixfyd537lcy2">
      <div className="flex items-start gap-3 px-1" data-id="element-4764" data-mp="3nphe50zl5lsu">
        <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4765" data-mp="3nq8xndfwrt2r">
          <LightbulbIcon className="w-5 h-5 text-[#6D4CF0]" strokeWidth={1.8} data-id="element-4766" data-mp="3dycys05zzyxh" />
        </span>
        <div className="min-w-0" data-id="element-4767" data-mp="2zz3mvcotrqzb">
          <p className="font-sans text-[15px] font-semibold text-[#121A3A]" data-id="element-4768" data-mp="3006b1curjfbj">{pick(a.title)}</p>
          <p className="font-sans text-[11.5px] text-[#3A4050] leading-snug mt-1" data-id="element-4769" data-mp="34wwqpz2no8wf">
            <strong className="font-semibold text-[#121A3A]" data-id="element-4770" data-mp="2lbczu6wdeyub">{pick(a.lead)}</strong>
            {pick(a.rest)}
          </p>
        </div>
      </div>
      <div className="rounded-xl bg-white p-3 flex items-start gap-3" data-id="element-4771" data-mp="2gf63n5ts11pk">
        <span className="w-8 h-8 rounded-full bg-[#4F46E5] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4772" data-mp="2udmacwrn2s7p">
          <ArrowRightIcon className="w-4 h-4 text-white" strokeWidth={2.2} data-id="element-4773" data-mp="2q9y189nczokc" />
        </span>
        <div className="min-w-0" data-id="element-4774" data-mp="349fr6sce57va">
          <p className="font-sans text-[13px] font-semibold text-[#4F46E5]" data-id="element-4775" data-mp="2zbxhv0ugoqmr">{pick(a.recoTitle)}</p>
          <p className="font-sans text-[11px] text-[#5B6478] leading-snug" data-id="element-4776" data-mp="3e0tyvnehxdzi">{pick(a.recoBody)}</p>
        </div>
      </div>
      <div className="rounded-xl bg-white p-3 flex items-start gap-3" data-id="element-4777" data-mp="3917dursfetg5">
        <BarChart3Icon className="w-6 h-6 text-[#4F46E5] shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-4778" data-mp="3n1i35qe37zz7" />
        <div className="flex-1 min-w-0" data-id="element-4779" data-mp="3i2fbr8r15s9p">
          <p className="font-sans text-[12px] font-semibold text-[#121A3A]" data-id="element-4780" data-mp="3i3qcezp6ijwl">{pick(a.impactTitle)}</p>
          <ul className="mt-2 grid grid-cols-3 gap-2" data-id="element-4781" data-mp="t8oy4u2o3177">
            {a.impacts.map((im, i) => <li key={i} className="flex items-start gap-1" data-id="element-4782" data-mp="y78mt28nar39" data-mpi={i}>
                {im.dir === 'down' ? <ArrowDownIcon className="w-4 h-4 text-[#E2476B] shrink-0" strokeWidth={2.2} aria-hidden="true" data-id="element-4783" data-mp="12bgd33ur4f3l" /> : <ArrowUpIcon className="w-4 h-4 text-[#22A04F] shrink-0" strokeWidth={2.2} aria-hidden="true" data-id="element-4784" data-mp="176vo7o1djwwb" />}
                <span className="font-sans text-[9.5px] text-[#5B6478] leading-tight" data-id="element-4785" data-mp="ek2lj04fcnjo">{pick(im.label)}</span>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}
const NAV = [{
  icon: LayoutDashboardIcon,
  label: {
    fr: 'Tableau de bord',
    en: 'Dashboard'
  }
}, {
  icon: FileStackIcon,
  label: {
    fr: 'Demandes de permis',
    en: 'Permit applications'
  }
}, {
  icon: ClipboardCheckIcon,
  label: {
    fr: 'Inspections',
    en: 'Inspections'
  }
}, {
  icon: CreditCardIcon,
  label: {
    fr: 'Paiements',
    en: 'Payments'
  }
}, {
  icon: FolderIcon,
  label: {
    fr: 'Documents',
    en: 'Documents'
  }
}, {
  icon: LineChartIcon,
  label: {
    fr: 'Analytique',
    en: 'Analytics'
  }
}, {
  icon: FileTextIcon,
  label: {
    fr: 'Rapports',
    en: 'Reports'
  }
}, {
  icon: SettingsIcon,
  label: {
    fr: 'Configuration',
    en: 'Settings'
  }
}];
export function DashboardSidebar({
  isFR
}: P) {
  const pick = pk(isFR);
  return <aside className="hidden lg:block w-[180px] shrink-0 border-r border-[#F0F0F5] bg-[#FAFAFD] px-3 py-5" data-id="element-4786" data-mp="31hdawhmeay5l">
      <p className="flex items-center gap-2 px-1 font-sans text-[13px] font-semibold text-[#121A3A]" data-id="element-4787" data-mp="2wpc2x7w9ftld">
        <span className="w-7 h-7 rounded-full bg-[#1E8A45] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4788" data-mp="2rsbu4p810cqk">
          <LeafIcon className="w-4 h-4 text-white" strokeWidth={2.2} data-id="element-4789" data-mp="2mu0o6pkuqmdm" />
        </span>
        <span className="truncate" data-id="element-4790" data-mp="2ipaun7do1jvv">{dashboardMeta.city}</span>
      </p>
      <ul className="mt-6 space-y-1" data-id="element-4791" data-mp="2dr98gdky14g6">
        {NAV.map((n, i) => {
        const Icon = n.icon;
        const on = i === 5;
        return <li key={i} className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 font-sans text-[11.5px] ${on ? 'bg-[#1B1E3A] text-white font-semibold' : 'text-[#3A4050]'}`} data-id="element-4792" data-mp="1t8sqap78k16t" data-mpi={i}>
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-4793" data-mp="1y1lsp6f8exfq" />
              <span className="truncate" data-id="element-4794" data-mp="1y3y9vhd613rq">{pick(n.label)}</span>
            </li>;
      })}
      </ul>
    </aside>;
}
export function DateRange({
  isFR
}: P) {
  return <span className="inline-flex items-center gap-2 rounded-lg border border-[#E6E7EF] bg-white px-3 py-1.5 font-sans text-[11px] text-[#121A3A] whitespace-nowrap" data-id="element-4795" data-mp="15ercilpg6k82">
      <CalendarIcon className="w-3.5 h-3.5 text-[#5B6478]" aria-hidden="true" data-id="element-4796" data-mp="1fa2sf7cldxhe" />
      {isFR ? dashboardMeta.range.fr : dashboardMeta.range.en}
      <ChevronDownIcon className="w-3.5 h-3.5 text-[#5B6478]" aria-hidden="true" data-id="element-4797" data-mp="2vrgrlhot1g7v" />
    </span>;
}